/** The `useParams()` value shape: each dynamic segment is a string, catch-all segments an array. */
export type RouteParams = Record<string, string | string[]>;

export interface IDeriveRoutePattern {
  fromPathname(pathname: string, params: RouteParams): string;
}

/**
 * Pure derivation of an App Router route *pattern* from a concrete pathname + its `useParams()` values,
 * substituting each param value back to its `[key]` (or `[...key]` catch-all) marker. This bounds the
 * `page` label cardinality — `builtbybert-web`'s `[...slug]` catch-all would otherwise emit one label
 * value per URL (§7.1, §5.1). Deterministic and DOM-free so it is unit-testable.
 * Substitution rules (applied per segment, longest catch-all values first so a catch-all's joined
 * value never gets partially matched by a shorter single param):
 * - a single dynamic param `foo=bar` in path `/x/bar` → `/x/[foo]`
 * - a catch-all param `slug=['a','b']` in path `/a/b` → `/[...slug]`
 * Segments with no matching param are kept verbatim (static routes pass through unchanged).
 */
export const DeriveRoutePattern: IDeriveRoutePattern = {
  /**
   * Derive the bounded route pattern.
   * @param pathname - The concrete pathname from `usePathname()` (eg: `/blog/my-post`).
   * @param params - The dynamic params from `useParams()` (eg: `{ slug: ['blog', 'my-post'] }`).
   * @returns The route pattern (eg: `/[...slug]`), or the pathname unchanged when it has no params.
   */
  fromPathname(pathname, params) {
    const segments = pathname.split('/').filter(segment => segment.length > 0);
    if (segments.length === 0) return '/';
    /**
     * Build a lookup from each concrete param segment value to its marker. Catch-all arrays are
     * expanded per element and also indexed by their joined value so a whole `/a/b/c` slug collapses
     * to a single `[...slug]` marker rather than three `[slug]` markers.
     */
    const catchAllJoined = new Map<string, string>();
    const singleValues = new Map<string, string>();
    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) catchAllJoined.set(value.join('/'), `[...${key}]`);
      else singleValues.set(value, `[${key}]`);
    }
    /** Collapse a catch-all first: if the trailing segments join to a known catch-all value, replace them wholesale. */
    for (const [joined, marker] of catchAllJoined) {
      const joinedSegments = joined.split('/').filter(segment => segment.length > 0);
      const start = _findSubsequence(segments, joinedSegments);
      if (start !== -1) segments.splice(start, joinedSegments.length, marker);
    }
    const patterned = segments.map(segment => singleValues.get(segment) ?? segment);
    return `/${patterned.join('/')}`;
  }
};

/**
 * Find the start index of `needle` within `haystack`, or `-1`. Empty needle → `-1` (nothing to collapse).
 * @param haystack - The path segments.
 * @param needle - The catch-all value segments to locate.
 */
const _findSubsequence = (haystack: string[], needle: string[]): number => {
  if (needle.length === 0 || needle.length > haystack.length) return -1;
  for (let i = 0; i <= haystack.length - needle.length; i += 1) {
    let match = true;
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) {
        match = false;
        break;
      }
    }
    if (match) return i;
  }
  return -1;
};
